/**
 * Helper function to check if a string is null, empty, or only whitespace
 * @param {string} str - The string to check
 * @returns {boolean} True if the string is empty or whitespace
 */
const isEmptyOrWhitespace = (str) => !str || str.trim().length === 0;

/**
 * Process codes with frequency data from filter report
 * @param {Array} codes - Array of code objects
 * @param {Object} filters - Filter object containing filter selections
 * @param {Object} filterReport - Filter report data with frequencies
 * @returns {Array} Codes enhanced with frequency data
 */
export const processCodesWithFrequencies = (codes, filters, filterReport) => {
  return (codes || []).map(code => {
    const frequencyData = filterReport?.codeFrequencies?.find(f => f.id === code.id);
    return {
      ...code,
      name: isEmptyOrWhitespace(code.name) ? 'Untitled Code' : code.name,
      checked: (filters?.codes || []).includes(code.id),
      count: frequencyData?.count || 0
    };
  });
};

/**
 * Process transcripts with frequency data from filter report
 * @param {Array} transcripts - Array of transcript objects
 * @param {Object} filters - Filter object containing filter selections
 * @param {Object} filterReport - Filter report data with frequencies
 * @returns {Array} Transcripts enhanced with frequency data
 */
export const processTranscriptsWithFrequencies = (transcripts, filters, filterReport) => {
  return (transcripts || []).map(transcript => {
    const transcriptData = filterReport?.transcriptFrequencies?.find(t => t.id === transcript.id);
    return {
      ...transcript,
      name: isEmptyOrWhitespace(transcript.name) ? 'Untitled Transcript' : transcript.name,
      checked: (filters?.transcripts || []).includes(transcript.id),
      count: transcriptData?.count || 0
    };
  });
};

/**
 * Process descriptors with frequency data from filter report
 * @param {Array} descriptorsData - Array of descriptor objects
 * @param {Object} filters - Filter object containing filter selections
 * @param {Object} filterReport - Filter report data with frequencies
 * @returns {Array} Descriptors enhanced with frequency data
 */
export const processDescriptorsWithFrequencies = (descriptorsData, filters, filterReport) => {
  if (!descriptorsData || !Array.isArray(descriptorsData)) {
    return [];
  }
  
  return descriptorsData.map(descriptor => {
    // Make sure multi_choices exists and is an array
    const multiChoices = descriptor.multi_choices || [];
    
    return {
      ...descriptor,
      multi_choices: multiChoices.map(choice => {
        // Check for frequency data from the filter report
        const choiceFrequency = filterReport?.multiChoiceFrequencies?.find(c => c.id === choice.id);
        
        // If the choice already has a count from the data source, use it
        // Otherwise try to get it from the filter report
        // Fall back to 0 if neither exists
        const count = choice.count !== undefined ? choice.count : 
                      (choiceFrequency?.count || 0);
                      
        return {
          ...choice,
          count,
          checked: filters?.multi_choices?.[descriptor.id]?.includes(choice.id) || false
        };
      })
    };
  });
};

/**
 * Process surveys with frequency data from filter report
 * @param {Array} surveys - Array of survey objects
 * @param {Object} filters - Filter object containing filter selections
 * @param {Object} filterReport - Filter report data with frequencies
 * @returns {Array} Surveys enhanced with frequency data
 */
export const processSurveysWithFrequencies = (surveys, filters, filterReport) => {
  return (surveys || []).map(survey => {
    const surveyData = filterReport?.surveyFrequencies?.find(s => s.id === survey.id);
    // Check title first, if it's empty/whitespace, use name instead
    const surveyName = !isEmptyOrWhitespace(survey.title) ? survey.title : survey.name;
    return {
      ...survey,
      name: isEmptyOrWhitespace(surveyName) ? 'Untitled Survey' : surveyName,
      checked: (filters?.surveys || []).includes(survey.id),
      count: surveyData?.count || 0
    };
  });
};

export default {
  processCodesWithFrequencies,
  processTranscriptsWithFrequencies,
  processDescriptorsWithFrequencies,
  processSurveysWithFrequencies
};
